import { useState, useRef } from "react";
import { FaPencilAlt } from "react-icons/fa";
import { RiDeleteBin5Fill } from "react-icons/ri";
import styles from "./Product.module.css";

export const VariantCard = ({
  variant,
  index,
  onFieldChange,
  onDelete,
  handleRemoveBg,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isRemovingBg, setIsRemovingBg] = useState(false);
  const imageInputRef = useRef(null);

  const resolveField = (preferred, fallback) =>
    variant && Object.prototype.hasOwnProperty.call(variant, preferred)
      ? preferred
      : fallback;

  const handleChange = (field, value) => {
    onFieldChange(index, field, value);
  };

  const handleColorChange = (value) => {
    const formattedValue = value?.startsWith("#") ? value : `#${value}`;
    const colorField = resolveField("hexColor", "hexCode");
    handleChange(colorField, formattedValue);
  };

  const toggleEdit = () => {
    setIsEditing((prev) => !prev);
  };

  const handleImageClick = () => {
    if (isEditing && imageInputRef.current) {
      imageInputRef.current.click();
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const updatedImages = [reader.result];
      handleChange("images", updatedImages);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const renderImage = () => {
    const image = variant?.images?.[0] || "";
    return (
      <div
        className={styles.variantImage}
        onClick={handleImageClick}
        style={{
          cursor: isEditing ? "pointer" : "default",
          opacity: isEditing ? 1 : 0.7,
        }}
      >
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          disabled={!isEditing}
          className={styles.imageInput}
          onChange={handleImageChange}
          style={{ display: "none" }}
        />
        {image ? (
          <>
            <img
              src={image}
              alt="variant"
              className={styles.variantImagePreview}
            />
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                if (isRemovingBg) return;
                try {
                  setIsRemovingBg(true);
                  await handleRemoveBg(0);
                } finally {
                  setIsRemovingBg(false);
                }
              }}
              disabled={isRemovingBg}
              style={{
                position: "absolute",
                bottom: 0,
                right: 0,
                backgroundColor: "#0f143a",
                color: "#fff",
                padding: "5px 10px",
                borderRadius: "0 0 5px 5px",
                cursor: isRemovingBg ? "not-allowed" : "pointer",
                border: "none",
                fontSize: "9px",
                fontWeight: "bold",
                width: "100%",
                margin: 0,
                opacity: isRemovingBg ? 0.7 : 1,
              }}
            >
              {isRemovingBg ? "Processing..." : "Remove Background"}
            </button>
          </>
        ) : (
          <span className={styles.uploadText}>Upload</span>
        )}
      </div>
    );
  };

  return (
    <div className={styles.variantCard}>
      <div className={styles.variantHeader}>
        <div className={styles.variantType}>
          <span>Variant Type:</span>
          {isEditing ? (
            <input
              type="text"
              value={variant?.type ?? ""}
              onChange={(e) => handleChange("type", e.target.value)}
              className={styles.variantTypeInput}
            />
          ) : (
            <span className={styles.variantTypeValue}>
              {variant?.type || "—"}
            </span>
          )}
        </div>
        <div className={styles.variantActions}>
          <button className={styles.variantButton} onClick={toggleEdit}>
            <FaPencilAlt size={14} />
            {isEditing ? "Done" : "Edit"}
          </button>
          <button
            className={styles.variantDeleteButton}
            onClick={() => onDelete(index)}
          >
            <RiDeleteBin5Fill size={14} />
            Delete
          </button>
        </div>
      </div>
      <div className={styles.variantFieldGroup}>
        <label>Variant Name:</label>
        {(() => {
          const nameField = resolveField("varName", "name");
          return (
            <input
              type="text"
              value={variant?.[nameField] ?? ""}
              onChange={(e) => handleChange(nameField, e.target.value)}
              disabled={!isEditing}
              className={styles.variantInput}
            />
          );
        })()}
      </div>
      <div className={styles.variantFieldGroup}>
        <label>SKU:</label>
        <input
          type="text"
          value={variant?.sku ?? ""}
          onChange={(e) => handleChange("sku", e.target.value)}
          disabled={!isEditing}
          className={styles.variantInput}
        />
      </div>
      <div className={`${styles.variantFieldGroup} ${styles.imagesRow}`}>
        <label>Image:</label>
        <div className={styles.variantImageRow}>{renderImage()}</div>
      </div>
      <div className={styles.variantFieldGroup}>
        <label>Hex Code:</label>
        {(() => {
          const colorField = resolveField("hexColor", "hexCode");
          const colorValue = variant?.[colorField] ?? "#000000";
          return (
            <div className={styles.hexInputRow}>
              <input
                type="text"
                value={colorValue}
                onChange={(e) => handleColorChange(e.target.value)}
                disabled={!isEditing}
                className={styles.variantInput}
              />
              <input
                type="color"
                value={colorValue}
                onChange={(e) => handleColorChange(e.target.value)}
                disabled={!isEditing}
                className={styles.colorPicker}
              />
            </div>
          );
        })()}
      </div>
    </div>
  );
};
